import React, { useState, useRef, useMemo, useEffect } from "react";
import { Clear, ExpandMore } from "@mui/icons-material";
import OperationsHub from "../OperationsHub";
import ContentHeader from "../ContentHeader";
import Toast from "../Toast";

const SENT_STATE = Object.freeze({
  SUCCESS: "SUCCESS",
  ERROR: "ERROR",
  NULL: "NULL",
});

export default function AppRegistrationHelper() {
  const [data, setData] = useState(null);
  const [sent, setSent] = useState(SENT_STATE.NULL);

  function onChange(e, types) {
    if (e) {
      const { name, value, type, checked } = e.target;
      setData((d) => ({ ...d, [name]: type === "checkbox" ? checked : value }));
    } else if (types) {
      setData((d) => ({ ...d, applicationType: types ? types : [] }));
    }
  }

  const onSubmit = async (e) => {
    e.preventDefault();

    setSent(SENT_STATE.ERROR);

    setTimeout(() => setSent(SENT_STATE.NULL), 5000);
  };

  return (
    <>
      <OperationsHub />

      <ContentHeader
        title={"Entra ID — App Registration Helper"}
        caption={"A one-stop, accessible form for quickly deploying your Azure app."}
      />

      <section className="app-registration-helper">
        <img src="/assets/img/entra_id_background.png" className="form-img" />

        <form onSubmit={onSubmit}>
          <div className="mb-6">
            <h2 className="text-lg font-semibold tracking-tight text-fg">Describe your app</h2>
            <p className="mt-1 text-sm text-fg-muted">
              Fields marked with <span className="text-rose-600">*</span> are required.
            </p>
          </div>

          <div className="field-collection">
            <Input
              id="display-name"
              name="displayName"
              placeholder="Display Name *"
              onChange={onChange}
            />
            <Input id="web-url" name="webUrl" placeholder="Web URL *" onChange={onChange} />

            <MultiDropdown
              defaultValue="Application Type *"
              icon={<ExpandMore />}
              onSelect={(selected) => onChange(undefined, selected)}
            >
              <MultiDropdownItem
                label="Browser Extension"
                value="publicClient"
                isSelected={false}
              />
              <MultiDropdownItem label="Single-page Application" value="spa" isSelected={false} />
              <MultiDropdownItem label="Web App" value="web-app" isSelected={false} />
            </MultiDropdown>

            <Textarea
              id="description"
              name="description"
              onChange={onChange}
              placeholder="Describe your app, its use case, and its target user."
              rows={6}
              required
            />
          </div>

          <button type="submit" className="send-form-btn">
            Send Request
          </button>
        </form>
      </section>

      {sent === SENT_STATE.SUCCESS && (
        <Toast
          type="success"
          header="Your application has been sent for registration!"
          message="You will be notified once your application has been registered."
          onClose={() => setSent(SENT_STATE.NULL)}
        />
      )}

      {sent === SENT_STATE.ERROR && (
        <Toast
          type="error"
          header="Something went wrong. Please try again."
          onClose={() => setSent(SENT_STATE.NULL)}
        />
      )}
    </>
  );
}

function Field({ label, name, children, required, full }) {
  return (
    <div className="form-field">
      <label htmlFor={name}>
        {label} {required && <span>*</span>}
      </label>
      {children}
    </div>
  );
}

function Input(props) {
  return <input {...props} className={["base-input-class", props.className || ""].join(" ")} />;
}

function Textarea(props) {
  return (
    <textarea
      {...props}
      className={["text-area base-input-class", props.className || ""].join(" ")}
    />
  );
}

function MultiDropdown({ defaultValue, icon, children, onSelect }) {
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [selectedLabels, setSelectedLabels] = useState([]);

  const selectedTypes = useRef([]);
  const documentClickRef = useRef(null);

  const clonedChildren = useMemo(
    () =>
      React.Children.map(children, (child) => {
        const itemProps = child?.props;

        return React.cloneElement(child, {
          value: itemProps["value"],
          label: itemProps["label"],
          isSelected: selectedLabels.includes(itemProps["label"]) ? true : false,
          onCheckboxToggle: (value, label) => {
            const newStates = [...selectedLabels];

            if (newStates.includes(label)) {
              newStates.splice(newStates.indexOf(label), 1);
            } else {
              newStates.push(label);
            }

            setSelectedLabels(newStates);
          },
        });
      }),
    [selectedLabels],
  );

  useEffect(() => {
    selectedTypes.current = [];

    for (const label of selectedLabels) {
      switch (label) {
        case "Browser Extension":
          selectedTypes.current.push("publicClient");
          break;
        case "Single-page Application":
          selectedTypes.current.push("spa");
          break;
        case "Web App":
          selectedTypes.current.push("web");
          break;
      }
    }

    onSelect(selectedTypes.current);
  }, [selectedLabels]);

  const selectedValues = useMemo(
    () =>
      selectedLabels.map((value) => (
        <li key={value} className="multi-dropdown-value">
          {value}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setSelectedLabels((labels) => labels.filter((val) => val != value));
            }}
          >
            <Clear fontSize="18px" />
          </button>
        </li>
      )),
    [selectedLabels],
  );

  /* useCloseOnOutsideClick(documentClickRef, (isVisible) => {

        if (isVisible) setIsDropdownVisible(false)
    }) */

  return (
    <div className="multi-dropdown-container">
      {selectedLabels.length > 0 ? (
        <fieldset
          className="multi-dropdown base-input-class"
          onClick={() => setIsDropdownVisible(!isDropdownVisible)}
        >
          <legend>Assigned Roles</legend>
          <ul className="multi-dropdown-selected">{selectedValues}</ul>
          <button
            style={{
              marginLeft: "auto",
              backgroundColor: "transparent",
              border: "none",
              color: "gray",
            }}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedLabels([]);
            }}
          >
            <Clear className="clear-btn" />
          </button>
          {icon}
        </fieldset>
      ) : (
        <span
          className="multi-dropdown base-input-class"
          onClick={() => setIsDropdownVisible(!isDropdownVisible)}
        >
          <p>{defaultValue}</p>
          {icon}
        </span>
      )}

      {isDropdownVisible && (
        <div ref={documentClickRef} className="multi-dropdown-options">
          {clonedChildren}
        </div>
      )}
    </div>
  );
}

function MultiDropdownItem(props) {
  return (
    <span className="multi-dropdown-item">
      <input
        type="checkbox"
        defaultChecked={props.isSelected}
        value={props.value}
        id={props.label}
        name={props.label}
        onChange={() => props.onCheckboxToggle(props.value, props.label)}
      />
      <label htmlFor={props.label}>{props.label}</label>
    </span>
  );
}
